
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button} from '@/components/ui/button';
import { Input} from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mic, MicOff, Send, Calendar as CalendarIcon, Loader } from 'lucide-react';
import { AudioRecorder, encodeAudioForAPI } from '@/utils/AudioRecorder';
import { ChatMessage, AudioState } from '@/types/chat';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { format, isToday, isYesterday } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DateRange } from 'react-day-picker';
import { Database } from '@/integrations/supabase/types';
import { Json } from '@/integrations/supabase/types';

type ConversationContext = {
  locations: string[];
  dates: { [key: string]: string };
  understanding: { [key: string]: Json };
  lastUpdate: string;
}

type ConversationContextRecord = Database['public']['Tables']['conversation_contexts']['Row'] & {
  context: ConversationContext;
}

const ChatInterface = () => {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [audioState, setAudioState] = useState<AudioState>({
    isRecording: false,
    isProcessing: false,
    isSpeaking: false,
  });
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const audioRecorderRef = useRef<AudioRecorder | null>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
 
  const { data: messages = [] } = useQuery({
    queryKey: ['messages'],
    queryFn: async () => {
      const { data: messages, error } = await supabase
        .from('messages')
        .select('*')
        .order('timestamp', { ascending: true });
 
      if (error) throw error;
 
      return messages?.map(msg => ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: new Date(msg.timestamp).getTime(),
        type: msg.type as 'text' | 'audio'
      })) || [];
    },
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 60, // Keep data in cache for 1 hour
  });
 
  const { data: userId } = useQuery({
    queryKey: ['userId'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id;
    },
  });
 
  const { data: conversationContext } = useQuery<ConversationContextRecord | null>({
    queryKey: ['conversationContext'],
    queryFn: async () => {
      if (!userId) return null;
      
      const { data: existingContext, error: contextError } = await supabase
        .from('conversation_contexts')
        .select('*')
        .eq('profile_id', userId)
        .eq('active', true)
        .maybeSingle();
 
      if (contextError && contextError.code !== 'PGRST116') throw contextError;
 
      if (existingContext) {
        return existingContext as ConversationContextRecord;
      }
 
      const initialContext: ConversationContext = {
        locations: [],
        dates: {},
        understanding: {},
        lastUpdate: new Date().toISOString()
      };
 
      const { data: newContext, error: insertError } = await supabase
        .from('conversation_contexts')
        .insert({
          profile_id: userId,
          context: initialContext as unknown as Json,
          active: true
        })
        .select()
        .single();
 
      if (insertError) throw insertError;
      return newContext as unknown as ConversationContextRecord;
    },
    enabled: !!userId,
  });
 
  const updateContext = async (newContext: Partial<ConversationContext>) => {
    if (!userId || !conversationContext) return;
 
    try {
      const updatedContext: ConversationContext = {
        locations: conversationContext.context.locations,
        dates: conversationContext.context.dates,
        understanding: conversationContext.context.understanding,
        lastUpdate: new Date().toISOString(),
        ...newContext
      };
 
      const { error } = await supabase
        .from('conversation_contexts')
        .update({
          context: updatedContext as unknown as Json
        })
        .eq('id', conversationContext.id);
 
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ['conversationContext'] });
    } catch (error) {
      console.error('Error updating context:', error);
      toast.error(t('assistant.errorUpdatingContext', 'Failed to update conversation context'));
    }
  };
 
  useEffect(() => {
    if (messageEndRef.current) {
      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages]);
 
  const persistMessage = async (message: ChatMessage) => {
    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          content: message.content,
          role: message.role,
          type: message.type,
          timestamp: new Date(message.timestamp).toISOString(),
          profile_id: (await supabase.auth.getUser()).data.user?.id
        });
 
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    } catch (error) {
      console.error('Error persisting message:', error);
      toast.error(t('assistant.errorSavingMessage', 'Failed to save message'));
    }
  };
 
  const handleDateSelect = (value: DateRange | undefined) => {
    if (value?.from) {
      const fromDate = format(value.from, 'MMMM do, yyyy');
      let dateText = `on ${fromDate}`;
      
      if (value.to) {
        const toDate = format(value.to, 'MMMM do, yyyy');
        dateText = `from ${fromDate} to ${toDate}`;
      }
      
      setInputText((prev) => prev + ' ' + dateText);
      setDateRange(value);
      if (value.to) {
        setShowCalendar(false);
      }
    }
  };
 
  const handleSendMessage = async (text: string) => {
    if (!text.trim() || !userId) return;
 
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
      type: 'text',
    };
 
    queryClient.setQueryData(['messages'], (old: ChatMessage[] = []) => [...old, newMessage]);
    setInputText('');
    setDateRange(undefined);
    setIsAiThinking(true);
 
    await persistMessage(newMessage);
 
    try {
      const response = await supabase.functions.invoke('chat', {
        body: { 
          message: text,
          userId: userId,
          dates: {
            departure: dateRange?.from?.toISOString(),
            return: dateRange?.to?.toISOString()
          },
          context: conversationContext?.context || {}
        },
      });
 
      if (response.error) throw response.error;
 
      if (response.data.updatedContext) {
        await updateContext(response.data.updatedContext);
      }
 
      const assistantMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: Date.now(),
        type: 'text',
      };
 
      queryClient.setQueryData(['messages'], (old: ChatMessage[] = []) => [...old, assistantMessage]);
      await persistMessage(assistantMessage);
 
      if (response.data.audioEnabled) {
        setAudioState(prev => ({ ...prev, isSpeaking: true }));
        await supabase.functions.invoke('text-to-speech', {
          body: { text: response.data.response },
        });
        setAudioState(prev => ({ ...prev, isSpeaking: false }));
      }
    } catch (error) {
      console.error('Error in chat:', error);
      toast.error(t('assistant.error', 'Failed to process message'));
    } finally {
      setIsAiThinking(false);
    }
  };
 
  const handleStartRecording = async () => {
    try {
      audioRecorderRef.current = new AudioRecorder((audioData) => {
        const encodedAudio = encodeAudioForAPI(audioData);
        supabase.functions.invoke('speech-to-text', {
          body: { audio: encodedAudio },
        });
      });
 
      await audioRecorderRef.current.start();
      setAudioState(prev => ({ ...prev, isRecording: true }));
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error(t('assistant.microphoneError', 'Could not access microphone'));
    }
  };
 
  const handleStopRecording = () => {
    if (audioRecorderRef.current) {
      audioRecorderRef.current.stop();
      setAudioState(prev => ({ ...prev, isRecording: false, isProcessing: true }));
    }
  };
 
  const groupedMessages = () => {
    const groups: { [key: string]: ChatMessage[] } = {};
    
    messages.forEach(message => {
      const date = new Date(message.timestamp);
      let key: string;
      
      if (isToday(date)) {
        key = 'Today';
      } else if (isYesterday(date)) {
        key = 'Yesterday';
      } else {
        key = format(date, 'dd-MM-yyyy');
      }
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(message);
    });
    
    return groups;
  };
 
  return (
    <div className="flex flex-col h-[calc(100vh-12rem)] max-w-3xl mx-auto">
      <ScrollArea className="flex-1 p-4 border rounded-lg mb-4" ref={scrollAreaRef}>
        <div className="space-y-6">
          {Object.entries(groupedMessages()).map(([date, dateMessages]) => (
            <div key={date} className="space-y-4">
              <div className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-10">
                <h3 className="text-sm font-medium text-muted-foreground py-2 text-center">
                  {date}
                </h3>
              </div>
              {dateMessages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    "flex w-full",
                    message.role === 'assistant' ? 'justify-start' : 'justify-end'
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-lg p-3",
                      message.role === 'assistant' 
                        ? "bg-secondary text-secondary-foreground" 
                        : "bg-primary text-primary-foreground"
                    )}
                  >
                    <div className="space-y-1">
                      <div>{message.content}</div>
                      <div className="text-xs opacity-50">
                        {format(new Date(message.timestamp), 'HH:mm')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {isAiThinking && (
            <div className="flex justify-start">
              <div className="bg-secondary text-secondary-foreground rounded-lg p-3">
                <Loader className="h-4 w-4 animate-spin" />
              </div>
            </div>
          )}
          <div ref={messageEndRef} />
        </div>
      </ScrollArea>
      
      <div className="flex gap-2 items-center">
        <Button
          variant={audioState.isRecording ? "destructive" : "outline"}
          size="icon"
          onClick={audioState.isRecording ? handleStopRecording : handleStartRecording}
          disabled={audioState.isProcessing}
        >
          {audioState.isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
 
        <Popover open={showCalendar} onOpenChange={setShowCalendar}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="px-2"
            >
              <CalendarIcon className="h-4 w-4" />
              <span className="sr-only">Select dates</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleDateSelect}
              numberOfMonths={2}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        
        <Input
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSendMessage(inputText);
            }
          }}
          placeholder={t('assistant.inputPlaceholder', 'Type your message...')}
          disabled={audioState.isRecording || audioState.isProcessing}
        />
        
        <Button
          onClick={() => handleSendMessage(inputText)}
          disabled={!inputText.trim() || audioState.isRecording || audioState.isProcessing}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default ChatInterface;
